import React from 'react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { flattenToAppURL, toPublicURL } from '@plone/volto/helpers/Url/Url';

import {
  resolveContentImage,
  resolveHeroVisual,
  resolvePublicPageUrl,
} from '../helpers';

type Props = {
  content: any;
};

export function resolveSocialImageUrl(url?: string, pageUrl?: string) {
  if (!url) return '';

  const flattened = flattenToAppURL(url);

  // Preserve images hosted by an external CDN. Plone URLs, on the other hand,
  // must use Volto's public origin so crawlers never receive an API URL or a
  // relative path in og:image.
  if (/^https?:\/\//i.test(url) && flattened === url) {
    return url;
  }

  if (!url.startsWith('/') && !/^https?:\/\//i.test(url) && pageUrl) {
    try {
      return new URL(url, `${pageUrl.replace(/\/$/, '')}/`).toString();
    } catch {
      return '';
    }
  }

  return toPublicURL(url);
}

export default function ContentSeo({ content }: Props) {
  const title = content?.title || '';
  const description = content?.description || '';
  const pageUrl = resolvePublicPageUrl(content);

  const rawImage = resolveContentImage(content) || resolveHeroVisual(content);
  const imageUrl = resolveSocialImageUrl(rawImage, pageUrl);

  if (!title) return null;

  return (
    <Helmet>
      <title>{title}</title>
      <link rel="canonical" href={pageUrl} />

      <meta name="theme-color" content="#1C9C6D" />
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={pageUrl} />

      {imageUrl ? <meta property="og:image" content={imageUrl} /> : null}
      {imageUrl ? (
        <meta property="og:image:secure_url" content={imageUrl} />
      ) : null}
      {imageUrl ? <meta property="og:image:alt" content={title} /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />

      {imageUrl ? <meta name="twitter:image" content={imageUrl} /> : null}
      {imageUrl ? <meta name="twitter:image:alt" content={title} /> : null}
    </Helmet>
  );
}
